// Our Components - Forms
import AutoRefiForm from 'components/Forms/NewLoanForms/auto/AutoRefiForm';
import CashOutRefi from 'components/Forms/NewLoanForms/otherLoanForms/CashOutRefiForm';
import ConstructionLoanInquiryForm from 'components/Forms/NewLoanForms/otherLoanForms/ConstructionLoanForm';
import FarmLoan from 'components/Forms/NewLoanForms/otherLoanForms/FarmLoanForm';
import HelocLoanForm from 'components/Forms/NewLoanForms/heloc/HelocForm';
import LotForm from 'components/Forms/NewLoanForms/otherLoanForms/LotLoanForm';
import MortgageRefinanceLoanForm from 'components/Forms/NewLoanForms/mortgage/MortgageRefinanceForm';
import NewAutoLoanForm from 'components/Forms/NewLoanForms/auto/NewAutoForm';
import NewMortgageLoanForm from 'components/Forms/NewLoanForms/mortgage/NewMortgageForm';
import NewPersonalLoan from 'components/Forms/NewLoanForms/personal/NewPersonalForm';
import PersonalRefinanceLoanForm from 'components/Forms/NewLoanForms/personal/PersonalRefinanceForm';
import ReverseMortgage from 'components/Forms/NewLoanForms/otherLoanForms/ReverseMortgageForm';
import SmallBusinessLoan from 'components/Forms/NewLoanForms/otherLoanForms/SmallBusinessForm';
import StudentLoanRefinanceLoanForm from 'components/Forms/NewLoanForms/student/StudentLoanRefinanceForm';
import SblocLoanForm from 'components/Forms/NewLoanForms/SBLOC/SblocLoanForm';
import OtherLoanForm from 'components/Forms/NewLoanForms/otherLoanForm/OtherLoanForm';

// Our Selectors
import selectCreditScorefromProfile from 'selectors/userProfile/selectCreditScorefromProfile';

// Our utils
import { roundLiabilityRate } from 'shared/utils';

// Our Constants
import {
	AUTO,
	CONSTRUCTION,
	COMMERCIAL,
	FARM,
	HELOC,
	LOT,
	MORTGAGE,
	OTHER_TRADELINE_TYPE,
	PERSONAL,
	SBLOC,
	STUDENT
} from 'shared/constants';

const FORM_TO_RENDER_MAP = {
	'Automotive Loan (New)': NewAutoLoanForm,
	'Automotive Loan (Refinance)': AutoRefiForm,
	'Cash Out Refinance': CashOutRefi,
	'Construction Loan': ConstructionLoanInquiryForm,
	'Commercial Loan': SmallBusinessLoan,
	'Farm Loan': FarmLoan,
	HELOC: HelocLoanForm,
	'Lot Loan': LotForm,
	'Mortgage Loan (New)': NewMortgageLoanForm,
	'Mortgage Loan (Refinance)': MortgageRefinanceLoanForm,
	'Personal Loan (New)': NewPersonalLoan,
	'Personal Loan (Refinance)': PersonalRefinanceLoanForm,
	'Reverse Mortgage': ReverseMortgage,
	'Securities Based Line': SblocLoanForm,
	'Student Loan (Refinance)': StudentLoanRefinanceLoanForm,
	Other: OtherLoanForm
};

const CATEGORY_TO_FORM_MAP = {
	'Automotive Loan (New)': AUTO,
	'Automotive Loan (Refinance)': AUTO,
	'Cash Out Refinance': MORTGAGE,
	'Construction Loan': CONSTRUCTION,
	'Commercial Loan': COMMERCIAL,
	'Farm Loan': FARM,
	HELOC,
	'Lot Loan': LOT,
	'Mortgage Loan (New)': MORTGAGE,
	'Mortgage Loan (Refinance)': MORTGAGE,
	'Personal Loan (New)': PERSONAL,
	'Personal Loan (Refinance)': PERSONAL,
	'Reverse Mortgage': MORTGAGE,
	'Securities Based Line': SBLOC,
	'Student Loan (Refinance)': STUDENT,
	Other: OTHER_TRADELINE_TYPE
};

const CATEGORY_TO_SELECTOR_MAP = {
	[AUTO]: ({ selectedClientLoan }) => {
		const clientVehicleProps = {
			vehicleId: selectedClientLoan?.vehicleInfo?.vehicleId,
			make: selectedClientLoan?.vehicleInfo?.make,
			model: selectedClientLoan?.vehicleInfo?.model,
			vin: selectedClientLoan?.vehicleInfo?.vin,
			licensePlate: selectedClientLoan?.vehicleInfo?.licensePlate,
			mileage: selectedClientLoan?.vehicleInfo?.mileage,
			condition: selectedClientLoan?.vehicleInfo?.condition,
			color: selectedClientLoan?.vehicleInfo?.color,
			state: selectedClientLoan?.vehicleInfo?.state,
			year: selectedClientLoan?.vehicleInfo?.year
		};
		return { clientVehicleInfo: clientVehicleProps };
	},
	[CONSTRUCTION]: () => ({}),
	[COMMERCIAL]: () => ({}),
	[FARM]: () => ({}),
	[HELOC]: ({ selectedClientLoan }) => {
		const clientHomeProps = {
			addressLine1Value: selectedClientLoan?.clientHome?.addressLine1,
			addressLine2Value: selectedClientLoan?.clientHome?.addressLine2,
			balanceTenureMonth:
				selectedClientLoan?.clientHome?.balanceTenureMonth,
			cityValue: selectedClientLoan?.clientHome?.city,
			dwellingType: selectedClientLoan?.clientHome?.dwellingType,
			estimatedValue: selectedClientLoan?.clientHome?.estimatedValue,
			primaryUseValue: selectedClientLoan?.clientHome?.propertyType,
			stateValue: selectedClientLoan?.clientHome?.state,
			zipCodeValue: selectedClientLoan?.clientHome?.zipcode
		};

		return { clientMortgageInfo: clientHomeProps };
	},
	[LOT]: () => ({}),
	[MORTGAGE]: ({ selectedClientLoan }) => {
		const clientHomeProps = {
			addressLine1Value: selectedClientLoan?.clientHome?.addressLine1,
			addressLine2Value: selectedClientLoan?.clientHome?.addressLine2,
			balanceTenureMonth:
				selectedClientLoan?.clientHome?.balanceTenureMonth,
			cityValue: selectedClientLoan?.clientHome?.city,
			dwellingType: selectedClientLoan?.clientHome?.dwellingType,
			estimatedValue: selectedClientLoan?.clientHome?.estimatedValue,
			primaryUseValue: selectedClientLoan?.clientHome?.propertyType,
			stateValue: selectedClientLoan?.clientHome?.state,
			zipCodeValue: selectedClientLoan?.clientHome?.zipcode
		};

		return { clientMortgageInfo: clientHomeProps };
	},
	[PERSONAL]: ({ clientProfileData }) => ({
		clientProfileData
	}),
	[SBLOC]: () => ({}),
	[STUDENT]: ({ clientProfileData }) => ({ clientProfileData }),
	[OTHER_TRADELINE_TYPE]: ({ clientProfileData }) => ({
		clientProfileData
	})
};

function LoanForms({
	advisorInfo,
	clientInfo,
	clientProfileData,
	clientWalletOverviewData,
	selectedLoanType,
	selectedClient,
	selectedClientLoan,
	isNewClientSelected,
	isClientNameReady
}) {
	const clientProfileDataWithFallBack = clientProfileData ?? {
		id: null,
		firstName: clientInfo.clientFirstName,
		lastName: clientInfo.clientLastName,
		annualIncome: clientInfo.clientAnnualIncome,
		assetsAmount: clientInfo.clientAssetsAmount,
		creditScore: clientInfo.clientCreditScore,
		address: null
	};

	const isClientNameProvided = isNewClientSelected ? isClientNameReady : true;

	const FormToRender = FORM_TO_RENDER_MAP[selectedLoanType];

	const clientInfoProps = {
		assetsAmount: isNewClientSelected
			? clientInfo.clientAssetsAmount
			: clientWalletOverviewData?.assetsAmount,
		annualIncome: isNewClientSelected
			? clientInfo.clientAnnualIncome
			: clientWalletOverviewData?.annualIncome,
		address: clientProfileDataWithFallBack?.address,
		clientId: clientProfileDataWithFallBack?.id,
		creditScoreValue: selectCreditScorefromProfile(
			clientProfileDataWithFallBack
		),
		dateOfBirth: clientProfileDataWithFallBack?.dob,
		emailAddressValue: clientProfileDataWithFallBack?.email,
		firstName: clientProfileDataWithFallBack?.firstName || '',
		lastName: clientProfileDataWithFallBack?.lastName || '',
		clientFullName: `${clientProfileDataWithFallBack?.firstName} ${clientProfileDataWithFallBack?.lastName}`,
		veteranStatusValue: clientProfileDataWithFallBack?.veteran
	};

	// Props that all forms require in order to operate
	const commonProps = {
		advisorInfo,
		isClientNameProvided,
		loanType: selectedLoanType,
		loanRequestId: selectedClientLoan?.loanRequestId
	};

	// Common Financial Data that all forms require
	const financialDataProps = {
		currentLoanInterestRateValue: selectedClientLoan?.interestRate
			? roundLiabilityRate(selectedClientLoan?.interestRate)
			: '',
		currentOutstandingBalance: selectedClientLoan?.outstandingBalance,
		currentLoanRemainingTermValue: selectedClientLoan?.balanceTenureMonth,
		currentLoanMonthlyPayment: selectedClientLoan?.monthlyPay,
		refinancingTradelineId: selectedClientLoan?.tradelineId
	};

	const isFormSelectionReady = selectedClient && selectedLoanType;

	// If the form selection is not ready, don't render anything
	if (!isFormSelectionReady) return null;

	// If the form selection is ready, render the form
	const selectedLoanTypeCategory = CATEGORY_TO_FORM_MAP[selectedLoanType];

	const loanSpecificProps = CATEGORY_TO_SELECTOR_MAP[
		selectedLoanTypeCategory
	]({ clientWalletOverviewData, clientProfileData, selectedClientLoan });

	return (
		<FormToRender
			{...commonProps}
			clientInfo={clientInfoProps}
			financialData={financialDataProps}
			{...loanSpecificProps}
		/>
	);
}

export default LoanForms;
